type JobProgressFinished = { completed: true }
type JobProgressFailed = { error: string }
type JobProgressProcessing = { completed: false }
type JobProgressCustom = { progress: any }
type JobProgressProgress = { progress: { current: number, total: number, status: string } }

export type JobProgressRaw =
    JobProgressFinished
    | JobProgressFailed
    | JobProgressProcessing
    | JobProgressCustom
    | JobProgressProgress;

export type ProgressInfoData = {
    status: string,
    percent: number
};

export class JobProgress {
    public readonly data: JobProgressRaw;

    constructor(jobProgress: JobProgressRaw) {
        this.data = jobProgress;
    }

    toProgressInfo(): ProgressInfoData | undefined {
        if ("progress" in this.data) {
            const current = this.data.progress?.current ?? 0;
            const total = this.data.progress?.total ?? 0;
            return {
                status: this.data.progress?.status ?? "",
                percent: Math.round(((current / total) ?? 0) * 100),
            }
        } else {
            return undefined;
        }
    }

    isCompleted(): boolean {
        if ("completed" in this.data) {
            return this.data.completed === true;
        } else {
            return false;
        }
    }

    isFinished(): this is { data: JobProgressFinished } {
        return (this.data as JobProgressFinished).completed === true;
    }

    isProcessing(): this is { data: JobProgressProcessing } {
        return (this.data as JobProgressProcessing).completed === false;
    }

    isFailed(): this is { data: JobProgressFailed } {
        return 'error' in this.data;
    }

    isCustomProgress(): this is { data: JobProgressCustom } {
        return 'progress' in this.data && !this.isProgress();
    }

    isProgress(): this is { data: JobProgressProgress } {
        const progress = (this.data as JobProgressProgress).progress;
        if (progress == null) return false;
        return (
            typeof progress.current === 'number' &&
            typeof progress.total === 'number' &&
            typeof progress.status === 'string'
        );
    }

    copy() {
        return new JobProgress(JSON.parse(JSON.stringify(this.data)));
    }
}
